import { useState } from 'react';

import { ListRounded } from '@mui/icons-material';
import { TextField, Select, Menu, MenuItem, FormControl, InputLabel, Box, IconButton, Tooltip } from '@mui/material';
import { OrdSortableList } from '@ragu-components/ord-components';
import { useTranslate } from 'react-admin';

import type { CallflowNodeMatchExitCauses, CallflowNodeInstruction } from '../dataProvider/types';

interface SortableInstructionsListProps {
  inputs: CallflowNodeInstruction[];
  inputDescriptionLabel?: string;
  inputMatchExitCausesLabel?: string;
  matchExitCauses: CallflowNodeMatchExitCauses;
  presets?: Array<Omit<CallflowNodeInstruction, 'id'>>;
  onChange: React.Dispatch<React.SetStateAction<CallflowNodeInstruction[]>>;
  onDelete: (deletedId: string) => void;
}

const PresetsMenu = ({
  presets,
  onChange,
}: {
  presets: Array<Omit<CallflowNodeInstruction, 'id'>>;
  onChange: (matchExitCauses: CallflowNodeMatchExitCauses, description: string) => void;
}): React.ReactElement => {
  const translate = useTranslate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Tooltip title={translate('callflows.components.sortableInstructionsList.presetsLabel')}>
        <IconButton
          onClick={handleClick}
          sx={{ ml: 0.5 }}
          size='small'
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
        >
          <ListRounded />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClick={() => {
          setAnchorEl(null);
        }}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        {presets.map((preset) => (
          <MenuItem
            key={preset.description}
            onClick={() => {
              setAnchorEl(null);
              onChange(preset.match_exit_causes, preset.description ?? '');
            }}
          >
            {preset.description}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SortableInstructionsInputs = ({
  input,
  index,
  inputDescriptionLabel,
  inputMatchExitCausesLabel,
  onUpdateInputs,
  presets,
  matchExitCauses,
}: {
  input: CallflowNodeInstruction;
  index: number;
  inputDescriptionLabel: string;
  inputMatchExitCausesLabel: string;
  presets: Array<Omit<CallflowNodeInstruction, 'id'>>;
  matchExitCauses: CallflowNodeMatchExitCauses;
  onUpdateInputs: (
    index: number,
    id: string,
    matchExitCauses: CallflowNodeInstruction['match_exit_causes'],
    description: string
  ) => void;
}): React.ReactElement => {
  const translate = useTranslate();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
      <TextField
        label={`${translate(inputDescriptionLabel)} ${index + 1}`}
        multiline
        onChange={(event) => {
          onUpdateInputs(index, input.id, input.match_exit_causes, event.target.value);
        }}
        variant='outlined'
        value={input.description ?? ''}
        sx={{ width: '100%' }}
      />
      <FormControl
        sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        size='small'
        variant='outlined'
      >
        <InputLabel>{`${translate(inputMatchExitCausesLabel)} ${index + 1}`}</InputLabel>
        <Select
          multiple
          label={`${translate(inputMatchExitCausesLabel)} ${index + 1}`}
          size='small'
          value={input.match_exit_causes}
          onChange={(event) => {
            onUpdateInputs(
              index,
              input.id,
              event.target.value as CallflowNodeInstruction['match_exit_causes'],
              input.description as unknown as string
            );
          }}
          sx={{
            minWidth: 0,
            width: '100%',
          }}
        >
          {matchExitCauses.map((option) => (
            <MenuItem key={option} value={option}>
              {translate(`callflows.components.sortableInstructionsList.match_exit_causes.${option}`)}
            </MenuItem>
          ))}
        </Select>
        {presets.length > 0 && (
          <PresetsMenu
            presets={presets}
            onChange={(matchExitCauses, description) => {
              onUpdateInputs(
                index,
                input.id,
                matchExitCauses,
                input.description !== undefined && input.description !== null && input.description !== ''
                  ? input.description
                  : description
              );
            }}
          />
        )}
      </FormControl>
    </Box>
  );
};

const SortableInstructionsList = ({
  inputs,
  inputDescriptionLabel = undefined,
  inputMatchExitCausesLabel = undefined,
  presets = [],
  onChange,
  onDelete,
  matchExitCauses,
}: SortableInstructionsListProps): React.ReactElement => {
  const translate = useTranslate();

  const onUpdateInputs = (
    index: number,
    id: string,
    matchExitCauses: CallflowNodeInstruction['match_exit_causes'],
    description: string
  ): void => {
    const updatedInputs = [...inputs];
    updatedInputs[index] = { id, description, match_exit_causes: matchExitCauses };
    onChange(updatedInputs);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', gap: 1, overflow: 'hidden' }}>
      <span>{translate('callflows.components.sortableInstructionsList.instructions')}</span>
      <Box sx={{ overflow: 'auto' }}>
        <OrdSortableList
          inputs={
            inputs as Array<{
              id: string;
              description: string;
              match_exit_causes: CallflowNodeInstruction['match_exit_causes'];
            }>
          }
          onChange={(newInputs) => {
            onChange(newInputs as unknown as CallflowNodeInstruction[]);
          }}
          emptyInput={{ id: '', description: '', match_exit_causes: [] }}
          onRemove={(input) => {
            onDelete(input.id);
          }}
          renderInput={(input, index) => (
            <SortableInstructionsInputs
              input={input as unknown as CallflowNodeInstruction}
              index={index}
              inputDescriptionLabel={
                inputDescriptionLabel ?? 'callflows.components.sortableInstructionsList.description'
              }
              inputMatchExitCausesLabel={
                inputMatchExitCausesLabel ?? 'callflows.components.sortableInstructionsList.matchExitCauses'
              }
              onUpdateInputs={onUpdateInputs}
              presets={presets}
              matchExitCauses={matchExitCauses}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default SortableInstructionsList;
